function normalizeText(value) {
  return String(value ?? "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
}

function matchesRule(group, ruleKeys = []) {
  const keys = [
    group.ruleName,
    group.id,
    group.label,
  ].map(normalizeText);

  return ruleKeys.some((ruleKey) => keys.includes(normalizeText(ruleKey)));
}

function pickAvailableValues(group, wantedValues = []) {
  const wanted = new Set(wantedValues.map(normalizeText));
  return (group.values ?? []).filter((value) => wanted.has(normalizeText(value)));
}

export const STRATEGY_LOGIC_OPTIONS = [
  {
    code: "CUSTOM",
    label: "Custom Strategy",
    action: "Manual",
    minCount: "User selected",
    businessRule: "Use the current market bias as background context and manually choose the signal values you want to test.",
    stockRule: "The backend will score stocks from the selected signal groups, price range, mode, and minimum score.",
    signals: {},
  },
  {
    code: "INTRADAY_BUY_CONTINUATION",
    label: "Momentum Continuation",
    action: "BUY",
    minCount: "8 continuation days",
    businessRule: "Used when the market is bullish, risk is acceptable, momentum is medium or high, and trend probability is at least 55%.",
    stockRule: "Pick leaders that repeatedly show tradeable score, strong buying, confirmed momentum, trending behavior, good liquidity, controlled volatility, and stable or pullback drawdown.",
    signals: {
      RELATIVE_STRENGTH: ["LEADER"],
      MOMENTUM_SCORE: ["CONFIRMED", "STRONG CONTINUATION"],
      TSI: ["STRONG", "VERY STRONG"],
      LIQUIDITY_SCORE: ["NORMAL", "STRONG", "VERY STRONG", "HIGH LIQUIDITY", "GOOD LIQUIDITY"],
      VOLATILITY_SCORE: ["NORMAL", "LOW", "CONTROLLED"],
      RECENT_DRAWDOWN: ["STABLE", "PULLBACK"],
    },
  },
  {
    code: "INTRADAY_BUY_PULLBACK",
    label: "Relative Strength Pullback",
    action: "BUY_ON_PULLBACK",
    minCount: "5 pullback days",
    businessRule: "Used as a secondary bullish plan when the market is supportive but chasing fresh breakouts is less attractive.",
    stockRule: "Pick leader stocks that are still trending but currently in pullback, with early or confirmed momentum and enough liquidity.",
    signals: {
      RELATIVE_STRENGTH: ["LEADER"],
      MOMENTUM_SCORE: ["EARLY", "CONFIRMED"],
      LIQUIDITY_SCORE: ["NORMAL", "STRONG", "VERY STRONG", "HIGH LIQUIDITY", "GOOD LIQUIDITY"],
      RECENT_DRAWDOWN: ["PULLBACK"],
    },
  },
  {
    code: "INTRADAY_BUY_EARLY_BUILDUP",
    label: "Breakout / Early Buildup",
    action: "WATCH_BUY",
    minCount: "8 buildup days",
    businessRule: "Used when the market is positive but a stock has not yet become a clean continuation buy.",
    stockRule: "Watch stocks where buying is building, momentum is early or confirmed, liquidity is acceptable, strength is neutral or leader, and drawdown is not damaged.",
    signals: {
      RELATIVE_STRENGTH: ["NEUTRAL", "LEADER", "STRONG"],
      MOMENTUM_SCORE: ["EARLY", "CONFIRMED"],
      TSI: ["MODERATE", "STRONG", "VERY STRONG"],
      LIQUIDITY_SCORE: ["NORMAL", "STRONG", "VERY STRONG", "HIGH LIQUIDITY", "GOOD LIQUIDITY"],
      RECENT_DRAWDOWN: ["STABLE", "PULLBACK"],
    },
  },
  {
    code: "WATCH_MIXED",
    label: "Range Trading",
    action: "WATCH",
    minCount: "No strong setup",
    businessRule: "Used when market bias is neutral, sideways probability is high, and momentum is low.",
    stockRule: "Keep mixed stocks on watch when they do not repeatedly qualify as continuation, pullback, short, sell, or avoid setups.",
    signals: {
      RELATIVE_STRENGTH: ["NEUTRAL", "STRONG"],
      MOMENTUM_SCORE: ["EARLY", "CONFIRMED"],
      VOLATILITY_SCORE: ["NORMAL", "LOW", "CONTROLLED"],
      LIQUIDITY_SCORE: ["NORMAL", "STRONG", "VERY STRONG", "HIGH LIQUIDITY", "GOOD LIQUIDITY"],
    },
  },
  {
    code: "AVOID_WEAK",
    label: "Defensive Quality / Avoid Weak",
    action: "AVOID",
    minCount: "15 avoid days",
    businessRule: "Used when the market is bearish or risk is elevated; current stock listing maps this defensive path to avoid weak names.",
    stockRule: "Classify stocks as avoid when weak or low-quality conditions repeat often and there are not enough continuation or buildup counts.",
    signals: {
      RELATIVE_STRENGTH: ["LAGGARD", "WEAK"],
      MOMENTUM_SCORE: ["NO ENTRY", "WEAK"],
      TSI: ["WEAK"],
      LIQUIDITY_SCORE: ["REJECT", "WEAK", "LOW LIQUIDITY"],
      VOLATILITY_SCORE: ["EXTREME", "RISKY", "HIGH RISK"],
      RECENT_DRAWDOWN: ["DAMAGED"],
    },
  },
  {
    code: "INTRADAY_SHORT_BREAKDOWN",
    label: "Short Breakdown",
    action: "SHORT_SELL",
    minCount: "5 breakdown days",
    businessRule: "Used for weak stocks when the broader plan is bearish or risk control is the priority.",
    stockRule: "Pick short candidates showing avoid score, strong selling or weak momentum, no-entry momentum, damaged drawdown, laggard strength, and non-extreme volatility.",
    signals: {
      RELATIVE_STRENGTH: ["LAGGARD", "WEAK"],
      MOMENTUM_SCORE: ["NO ENTRY", "WEAK"],
      TSI: ["WEAK"],
      LIQUIDITY_SCORE: ["NORMAL", "STRONG", "VERY STRONG", "HIGH LIQUIDITY", "GOOD LIQUIDITY"],
      VOLATILITY_SCORE: ["NORMAL", "LOW", "CONTROLLED"],
      RECENT_DRAWDOWN: ["DAMAGED"],
    },
  },
  {
    code: "INTRADAY_SELL_OVEREXTENDED",
    label: "Overextended Sell / Avoid Chase",
    action: "SELL_OR_AVOID_CHASE",
    minCount: "4 exhaustion days",
    businessRule: "Used when a stock has already moved too far and fresh entry risk is high.",
    stockRule: "Flag stocks where exhaustion, risky spike, overextended momentum, or overextended return appears repeatedly.",
    signals: {
      MOMENTUM_SCORE: ["RISKY SPIKE", "OVEREXTENDED"],
      TSI: ["EXTREME"],
      RVOL: ["SPIKE"],
    },
  },
];

const STRATEGY_LABEL_ALIASES = {
  "BUY NOW": "INTRADAY_BUY_CONTINUATION",
  "BUY PULLBACK": "INTRADAY_BUY_PULLBACK",
  "WATCH BUY": "INTRADAY_BUY_EARLY_BUILDUP",
  "SHORT SELL": "INTRADAY_SHORT_BREAKDOWN",
  "SELL / AVOID": "INTRADAY_SELL_OVEREXTENDED",
  "AVOID": "AVOID_WEAK",
  "WATCH": "WATCH_MIXED",
  "MOMENTUM CONTINUATION": "INTRADAY_BUY_CONTINUATION",
  "BREAKOUT": "INTRADAY_BUY_EARLY_BUILDUP",
  "RELATIVE STRENGTH PULLBACK": "INTRADAY_BUY_PULLBACK",
  "WEAK RELATIVE STRENGTH": "AVOID_WEAK",
  "DAMAGED DRAWDOWN": "AVOID_WEAK",
  "LOW LIQUIDITY SCALPS": "AVOID_WEAK",
  "RANGE TRADING": "WATCH_MIXED",
  "SELECTIVE CONTINUATION": "INTRADAY_BUY_CONTINUATION",
  "DEFENSIVE QUALITY": "AVOID_WEAK",
  "DEFENSIVE QUALITY / AVOID WEAK": "AVOID_WEAK",
  "CONFIRMED MEAN REVERSION": "WATCH_MIXED",
  "INTRADAY TREND CONFIRMATION": "INTRADAY_BUY_CONTINUATION",
  "AGGRESSIVE MOMENTUM": "INTRADAY_BUY_CONTINUATION",
  "EARLY LONG CHASE": "INTRADAY_SELL_OVEREXTENDED",
  "OVERNIGHT RISK": "AVOID_WEAK",
};

export function buildStrategySignalSelection(signals = [], strategyLogic) {
  const presetSignals = strategyLogic?.signals ?? {};

  return signals.reduce((selection, group) => {
    const entry = Object.entries(presetSignals).find(([ruleName]) => matchesRule(group, [ruleName]));
    selection[group.id] = entry ? pickAvailableValues(group, entry[1]) : [];
    return selection;
  }, {});
}

export function getStrategySignalRows(signals = [], strategyLogic) {
  const presetSignals = strategyLogic?.signals ?? {};

  return Object.entries(presetSignals).map(([ruleName, values]) => {
    const group = signals.find((signalGroup) => matchesRule(signalGroup, [ruleName]));
    const availableValues = group ? pickAvailableValues(group, values) : [];
    return {
      ruleName,
      label: group?.label ?? ruleName.replace(/_/g, " "),
      expectedValues: values,
      availableValues,
      status: !group ? "missing" : availableValues.length ? "available" : "unavailable",
    };
  });
}

export function getStrategyByCode(code) {
  return STRATEGY_LOGIC_OPTIONS.find((strategy) => strategy.code === code) ?? STRATEGY_LOGIC_OPTIONS[0];
}

export function getStrategyByLabelOrCode(value) {
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) return STRATEGY_LOGIC_OPTIONS[0];

  return (
    STRATEGY_LOGIC_OPTIONS.find((strategy) => strategy.code === STRATEGY_LABEL_ALIASES[normalizedValue]) ??
    STRATEGY_LOGIC_OPTIONS.find((strategy) => normalizeText(strategy.code) === normalizedValue) ??
    STRATEGY_LOGIC_OPTIONS.find((strategy) => normalizeText(strategy.label) === normalizedValue) ??
    STRATEGY_LOGIC_OPTIONS.find((strategy) => {
      const label = normalizeText(strategy.label);
      return label.includes(normalizedValue) || normalizedValue.includes(label);
    }) ??
    STRATEGY_LOGIC_OPTIONS[0]
  );
}
